import { useState, useRef, useEffect } from "react";
import { ChevronLeft, Sparkles, BookOpen, RotateCcw, Save } from "lucide-react";

type View = "menu" | "scene" | "chat" | "review";

interface DialogueTurn {
  npc: string;
  user: string;
}

interface Scene {
  id: string;
  title: string;
  subtitle: string;
  emoji: string;
  color: string;
  description: string;
  scenario: string;
  alternativeScenarios: string[];
  echoHint: string;
  turns: DialogueTurn[];
  observation: string;
  suggestion: string;
  whyBetter: string[];
  finalAbility: string;
  recentPractice?: { content: string; time: string };
  stats?: string;
}

const scenes: Scene[] = [
  {
    id: "workplace",
    title: "🌿 场景复盘",
    subtitle: "那些想重来一次的时刻。",
    emoji: "🗣️",
    color: "#EAF4FF",
    description: "把遗憾重新说一次。",
    scenario: "今天下午的例会上，同事直接汇报了你准备了一周的方案。老板听完后，看向你：\n\"你还有什么想补充的吗？\"",
    alternativeScenarios: [
      "客户在会议上突然提出了一个超出预算的需求。",
      "朋友借了你一本书，却迟迟不还。",
    ],
    recentPractice: { content: "开会时没有说出口的话", time: "昨天" },
    stats: "已完成 18 次排练",
    echoHint: "先别急着证明自己。\n先把事实说清楚，再表达自己的立场。",
    turns: [
      { npc: "这个方案主要就是他负责的吗？", user: "没事，我觉得他说得挺完整的……" },
      { npc: "所以这个方案前期不是你负责的吗？", user: "我也参与了一些。" },
      { npc: "我有点没听明白。\n你的具体工作是什么？", user: "前期资料其实是我整理的。" },
      { npc: "那为什么刚才没有说？", user: "我怕打断别人。" },
      { npc: "明白了。\n那后续你准备怎么推进？", user: "后续我会继续完善方案，也希望后面的汇报能一起参与。" },
    ],
    observation: "今天你不是没有能力表达。\n而是第一反应总是选择退让。\n随着对话继续，你开始慢慢讲事实，而不是解释自己。\n真正改变局面的，不是更强硬，而是更清楚。",
    suggestion: "\"我补充一下，这个方案前期主要由我负责整理，后续我们一起完善到了现在。\"",
    whyBetter: ["先陈述事实。", "不否定别人。", "同时表达自己的贡献。"],
    finalAbility: "表达立场，不等于制造冲突。",
  },
  {
    id: "friend",
    title: "💬 对话练习",
    subtitle: "每一句认真说出口的话，都会成为未来的本能。",
    emoji: "💬",
    color: "#DDF6E8",
    description: "提前练习那些重要的对话。",
    scenario: "朋友晚上十一点发来消息：\n最近什么都不想干。",
    alternativeScenarios: [
      "朋友失恋了，打电话来倾诉。",
      "父母催你结婚，你不想聊这个话题。",
    ],
    recentPractice: { content: "安慰失恋的朋友", time: "2天前" },
    stats: "最近新增 3 个场景",
    echoHint: "今天先别急着解决问题。\n试着陪他把话继续说下去。",
    turns: [
      { npc: "最近什么都不想干。", user: "怎么了？" },
      { npc: "不知道。\n就是觉得特别累。", user: "工作太累了吗？" },
      { npc: "工作只是一个原因吧。\n感觉每天醒来都不知道为什么起床。", user: "最近发生什么了吗？" },
      { npc: "其实也没发生什么。\n我只是觉得没人知道我现在这样。", user: "听起来，你最近真的撑了很久。" },
      { npc: "嗯……\n谢谢你愿意听我说这些。", user: "如果你想说，我一直都在。" },
    ],
    observation: "一开始，你一直在寻找原因。\n后来，你开始把注意力放回他的感受。\n最后一句：\n\"如果你想说，我一直都在。\"\n真正让对话发生了变化。\n因为陪伴，有时候比建议更重要。",
    suggestion: "当他说：\n\"每天醒来都不知道为什么起床。\"\n可以试试：\n\"听起来，你真的已经撑了很久。\"\n不是因为它更高级。\n而是因为它先接住了情绪。",
    whyBetter: ["先回应情绪。", "不急于给建议。", "让对方感受到被看见。"],
    finalAbility: "一句真正被听见的话，比一句正确的话更重要。",
  },
  {
    id: "observation",
    title: "🌊 灵感观察",
    subtitle: "好的表达，始于认真看见。",
    emoji: "👁️",
    color: "#F2EAFF",
    description: "好的表达，始于认真看见。",
    scenario: "请不要说：\"好美。\"\n试着告诉我，是什么让你停下来了？",
    alternativeScenarios: [
      "清晨的窗外，有一只鸟停在枝头。",
      "走在路上，一片落叶飘到你面前。",
    ],
    recentPractice: { content: "海边的傍晚", time: "今天" },
    stats: "收录了 26 条属于你的表达",
    echoHint: "先别急着评价。\n先说你真正看见了什么。",
    turns: [
      { npc: "", user: "今天的海好美。" },
      { npc: "如果闭上眼睛，你还能记住什么？", user: "风很舒服。" },
      { npc: "有没有一个声音，让这一刻变得特别？", user: "海浪的声音。" },
      { npc: "有没有一个细节，让这一刻变得难忘？", user: "夕阳一点一点掉进海里。" },
      { npc: "现在，请把这些感受连成一句属于你的表达。", user: "海风没有说话，却把今天吹得越来越轻。" },
    ],
    observation: "今天不是你学会了更多词汇。\n而是你开始注意那些原本会被忽略的细节。\n真正打动人的表达，\n往往不是\"好美\"，\n而是那个只有你看见的瞬间。",
    suggestion: "今天，我想替你保存这一句话：\n\"海风没有说话，却把今天吹得越来越轻。\"\n这不是一句名言。\n这是今天属于你的表达。",
    whyBetter: ["描述了具体的感受。", "用了拟人的手法。", "留下了想象空间。"],
    finalAbility: "所有动人的表达，都始于认真看见世界。",
  },
];

export default function BattlePage() {
  const [view, setView] = useState<View>("menu");
  const [currentScene, setCurrentScene] = useState<Scene | null>(null);
  const [revealedCount, setRevealedCount] = useState(0);
  const [npcVisibleUpTo, setNpcVisibleUpTo] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [customScenario, setCustomScenario] = useState("");
  const [animatingTurn, setAnimatingTurn] = useState<number | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [revealedCount, npcVisibleUpTo]);

  const startScene = (scene: Scene) => {
    setCurrentScene(scene);
    setRevealedCount(0);
    setNpcVisibleUpTo(0);
    setShowHint(false);
    setCustomScenario(scene.scenario);
    setAnimatingTurn(null);
    setView("scene");
  };

  const startChat = () => {
    setView("chat");
  };

  const nextTurn = () => {
    if (!currentScene) return;

    if (revealedCount >= currentScene.turns.length) {
      setView("review");
      return;
    }

    // 1) User response animates in
    setAnimatingTurn(revealedCount);

    setTimeout(() => {
      setAnimatingTurn(null);
      setRevealedCount((prev) => prev + 1);

      // 2) After a pause, the next NPC question appears
      setTimeout(() => {
        setNpcVisibleUpTo((prev) => prev + 1);
        
        // 3) Echo hint appears after NPC response for first turn
        if (revealedCount === 0) {
          setTimeout(() => {
            setShowHint(true);
          }, 800);
        }
      }, 650);
    }, 400);
  };

  const handleSaveExpression = () => {
    setShowToast(true);
    setTimeout(() => setShowToast(false), 2000);
  };

  const goBack = () => {
    setView("menu");
    setCurrentScene(null);
    setRevealedCount(0);
    setNpcVisibleUpTo(0);
    setShowHint(false);
    setAnimatingTurn(null);
  };

  // ===== Menu View =====
  if (view === "menu") {
    return (
      <div className="relative min-h-full">
        <div className="absolute inset-0 bg-diffused" style={{ 
          background: `
            radial-gradient(ellipse at 20% 10%, rgba(189,169,255,0.35) 0%, transparent 55%),
            radial-gradient(ellipse at 80% 20%, rgba(110,231,183,0.28) 0%, transparent 55%),
            radial-gradient(ellipse at 30% 80%, rgba(217,236,255,0.25) 0%, transparent 55%),
            radial-gradient(ellipse at 70% 70%, rgba(197,168,255,0.2) 0%, transparent 50%),
            radial-gradient(ellipse at 45% 50%, rgba(110,231,183,0.15) 0%, transparent 40%),
            linear-gradient(180deg, #F5F2FA 0%, #F8F0FA 100%)
          ` 
        }} />
        <div className="glow-orb" style={{ width: 280, height: 280, background: "#C5A8FF", top: -80, right: -100, opacity: 0.5 }} />
        <div className="glow-orb" style={{ width: 220, height: 220, background: "#6EE7B7", top: 280, left: -70, opacity: 0.42 }} />
        <div className="glow-orb" style={{ width: 200, height: 200, background: "#EAF4FF", top: 580, right: -60, opacity: 0.35 }} />
        <div className="glow-orb" style={{ width: 180, height: 180, background: "#DDF6E8", top: 460, left: 20, opacity: 0.3 }} />

        <div className="relative z-10">
          <header className="px-5 pt-5 pb-6">
            <h1 className="text-[24px] font-bold text-echo-text leading-tight">🎭 排练室</h1>
            <p className="text-echo-text/45 text-[13px] mt-1">那些脱口而出的从容，都来自无人看见的练习。</p>
            <div className="mt-4 px-4 py-2.5 rounded-full bg-white/40 flex items-center gap-2">
              <span className="text-[12px] text-echo-text/50">🌿 今天适合慢一点表达。</span>
            </div>
          </header>

          <div className="px-5 space-y-4 pb-8">
            {scenes.map((scene) => (
              <div
                key={scene.id}
                className="glass-card rounded-card p-5 cursor-pointer animate-spring relative overflow-hidden"
                onClick={() => startScene(scene)}
              >
                <div 
                  className="absolute inset-0 opacity-10"
                  style={{ background: `radial-gradient(circle at 80% 20%, ${scene.color}, transparent 60%)` }}
                />
                <div className="relative z-10">
                  <div className="flex items-start gap-3 mb-3">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: `${scene.color}30` }}
                    >
                      <span className="text-[26px]">{scene.emoji}</span>
                    </div>
                    <div>
                      <h2 className="text-[16px] font-bold text-echo-text">{scene.title}</h2>
                      <p className="text-echo-text/50 text-[11px] mt-0.5">{scene.subtitle}</p>
                    </div>
                  </div>
                  <p className="text-echo-text/40 text-[12px] italic mb-3">
                    {scene.description}
                  </p>
                  {scene.recentPractice && (
                    <div className="pt-2.5 border-t border-white/20">
                      <p className="text-[10px] text-[#8B7355] mb-1 flex items-center gap-1">
                        <span>🕒</span> 最近练习
                      </p>
                      <div className="flex justify-between items-center">
                        <p className="text-echo-text/60 text-[12px]">{scene.recentPractice.content}</p>
                        <p className="text-echo-text/35 text-[10px]">{scene.recentPractice.time}</p>
                      </div>
                    </div>
                  )}
                  {scene.stats && (
                    <div className="mt-2 pt-1.5 border-t border-white/15">
                      <p className="text-[10px] text-echo-text/40">{scene.stats}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          <section className="px-5 pb-20">
            <p className="text-center text-echo-text/25 text-[11px]">
              在这里，没有失败，只有可以重来一次的人生。
            </p>
          </section>
        </div>
      </div>
    );
  }

  // ===== Scene View =====
  if (view === "scene" && currentScene) {
    return (
      <div className="relative min-h-full">
        <div className="absolute inset-0 bg-diffused" style={{ 
          background: `
            radial-gradient(ellipse at 20% 10%, rgba(189,169,255,0.3) 0%, transparent 55%),
            radial-gradient(ellipse at 80% 20%, rgba(110,231,183,0.25) 0%, transparent 55%),
            radial-gradient(ellipse at 30% 80%, rgba(217,236,255,0.22) 0%, transparent 55%),
            linear-gradient(180deg, #F5F2FA 0%, #F8F0FA 100%)
          ` 
        }} />

        <div className="relative z-10 flex flex-col h-full">
          <header className="px-5 pt-5 pb-4">
            <button
              onClick={goBack}
              className="w-9 h-9 rounded-full bg-white/50 flex items-center justify-center animate-spring"
            >
              <ChevronLeft size={18} strokeWidth={2} className="text-echo-text/60" />
            </button>
          </header>

          <div className="flex-1 px-5 flex flex-col justify-center">
            <div className="glass-card rounded-card p-6 mb-6">
              <div className="flex items-center gap-3 mb-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ background: `${currentScene.color}30` }}
                >
                  <span className="text-[24px]">{currentScene.emoji}</span>
                </div>
                <div>
                  <h2 className="text-[17px] font-bold text-echo-text">{currentScene.title}</h2>
                  <p className="text-echo-text/40 text-[11px]">{currentScene.subtitle}</p>
                </div>
              </div>
              <div className="p-4 rounded-xl bg-[#FFF3D8]/30 border border-[#FFF3D8]/50">
                <p className="text-[#8B7355] text-[13px] mb-2 flex items-center gap-1">
                  <span>💡</span> 预设场景（可编辑）
                </p>
                <textarea
                  value={customScenario}
                  onChange={(e) => setCustomScenario(e.target.value)}
                  className="w-full h-28 bg-white/30 rounded-xl p-3 text-echo-text/70 text-[14px] leading-relaxed resize-none focus:outline-none focus:ring-2 focus:ring-[#FFF3D8]/60"
                  placeholder="描述你想排练的场景……"
                />
              </div>
              <p className="text-echo-text/35 text-[11px] mt-3 text-center">
                确认此场景后进入对话排练
              </p>
            </div>

            <div className="flex flex-col gap-3">
              {currentScene.alternativeScenarios && currentScene.alternativeScenarios.length > 0 && (
                <button
                  onClick={() => {
                    const randomIndex = Math.floor(Math.random() * currentScene.alternativeScenarios.length);
                    setCustomScenario(currentScene.alternativeScenarios[randomIndex]);
                  }}
                  className="w-full py-2.5 rounded-full bg-white/30 text-echo-text/50 text-[13px] hover:bg-white/40 transition-colors"
                >
                  ✨ 换一个场景
                </button>
              )}
              <button
                onClick={startChat}
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#DDF6E8] to-[#F2EAFF] text-echo-text font-medium text-[15px] animate-spring"
              >
                确认并开始排练
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== Chat View =====
  if (view === "chat" && currentScene) {
    const isComplete = revealedCount >= currentScene.turns.length;

    return (
      <div className="relative min-h-full">
        <div className="absolute inset-0 bg-diffused" style={{
          background: `
            radial-gradient(ellipse at 20% 10%, rgba(189,169,255,0.3) 0%, transparent 55%),
            radial-gradient(ellipse at 80% 20%, rgba(110,231,183,0.25) 0%, transparent 55%),
            radial-gradient(ellipse at 30% 80%, rgba(217,236,255,0.22) 0%, transparent 55%),
            linear-gradient(180deg, #F5F2FA 0%, #F8F0FA 100%)
          `
        }} />

        <div className="relative z-10 flex flex-col h-full">
          <header className="px-5 pt-5 pb-3">
            <div className="flex items-center justify-between">
              <button
                onClick={goBack}
                className="w-9 h-9 rounded-full bg-white/50 flex items-center justify-center animate-spring"
              >
                <ChevronLeft size={18} strokeWidth={2} className="text-echo-text/60" />
              </button>
              <span className="text-[12px] text-echo-text/40">{currentScene?.title}</span>
            </div>
          </header>

          <div className="flex-1 overflow-y-auto px-5 pb-4 space-y-4">
            {/* Scene description — always shown first */}
            <div className="flex justify-start animate-fade-slide">
              <div className="max-w-[85%] px-4 py-3 rounded-3xl bg-white/50 text-echo-text/75">
                <p className="text-[14px] leading-relaxed whitespace-pre-line">
                  {customScenario}
                </p>
              </div>
            </div>

            {/* Dialogue turns: NPC speaks first → User responds */}
            {currentScene.turns.map((round, idx) => {
              const npcVisible = npcVisibleUpTo >= idx;
              const userVisible = revealedCount > idx;
              if (!npcVisible && !userVisible) return null;

              return (
                <div key={idx} className="space-y-2">
                  {/* NPC question */}
                  {round.npc && npcVisible ? (
                    <div className={`flex justify-start ${npcVisibleUpTo === idx && revealedCount === idx ? "animate-fade-slide" : ""}`}>
                      <div className="max-w-[85%] px-4 py-3 rounded-3xl bg-white/50 text-echo-text/75">
                        <p className="text-[14px] leading-relaxed whitespace-pre-line">{round.npc}</p>
                      </div>
                    </div>
                  ) : null}

                  {/* User response */}
                  {userVisible ? (
                    <div className={`flex justify-end ${animatingTurn === idx ? "animate-fade-slide" : ""}`}>
                      <div className="max-w-[85%] px-4 py-3 rounded-3xl bg-[#DDF6E8]/30 text-echo-text">
                        <p className="text-[14px] leading-relaxed whitespace-pre-line">{round.user}</p>
                      </div>
                    </div>
                  ) : null}
                </div>
              );
            })}

            {/* Echo hint — shown after first response */}
            {showHint && revealedCount >= 1 && (
              <div className="animate-fade-slide">
                <div className="mx-auto max-w-[85%] px-4 py-4 rounded-2xl bg-[#F2EAFF]/30 border border-[#C5A8FF]/20">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={12} strokeWidth={2} className="text-[#8B5CF6]" />
                    <span className="text-[12px] font-medium text-[#8B5CF6]">🌱 Echo 今日教练提示</span>
                  </div>
                  <p className="text-echo-text/70 text-[13px] leading-relaxed whitespace-pre-line">
                    {currentScene.echoHint}
                  </p>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="px-5 pb-4">
            {isComplete ? (
              <button
                onClick={nextTurn}
                className="w-full py-3.5 rounded-full bg-gradient-to-r from-[#DDF6E8] to-[#F2EAFF] text-echo-text font-medium text-[15px] animate-spring"
              >
                查看回响
              </button>
            ) : (
              <div className="space-y-2">
                <p className="text-[11px] text-[#8B7355] flex items-center gap-1 px-1">
                  <span>💡</span> 预设回应，点击发送
                </p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 glass-card rounded-full px-4 py-3">
                    <p className="text-echo-text/50 text-[13px] italic">
                      {currentScene.turns[revealedCount]?.user || "写下你的回应……"}
                    </p>
                  </div>
                  <button
                    onClick={nextTurn}
                    className="w-12 h-12 rounded-full bg-gradient-to-r from-[#DDF6E8] to-[#F2EAFF] flex items-center justify-center animate-spring"
                  >
                    <span className="text-[16px]">→</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== Review View =====
  if (view === "review" && currentScene) {
    return (
      <div className="relative min-h-full">
        <div className="absolute inset-0 bg-diffused" style={{ 
          background: `
            radial-gradient(ellipse at 20% 10%, rgba(189,169,255,0.3) 0%, transparent 55%),
            radial-gradient(ellipse at 80% 20%, rgba(110,231,183,0.25) 0%, transparent 55%),
            radial-gradient(ellipse at 30% 80%, rgba(217,236,255,0.22) 0%, transparent 55%),
            linear-gradient(180deg, #F5F2FA 0%, #F8F0FA 100%)
          ` 
        }} />

        <div className="relative z-10 px-5 pt-5 pb-20">
          <header className="mb-6">
            <button
              onClick={goBack}
              className="w-9 h-9 rounded-full bg-white/50 flex items-center justify-center animate-spring mb-4"
            >
              <ChevronLeft size={18} strokeWidth={2} className="text-echo-text/60" />
            </button>
            <h1 className="text-[22px] font-bold text-echo-text leading-tight">🌱 Echo 回响</h1>
            <p className="text-echo-text/40 text-[13px] mt-1">今天的排练，让我们一起回顾。</p>
          </header>

          <section className="mb-5">
            <div className="glass-card rounded-card p-5">
              <h3 className="text-[14px] font-medium text-echo-text/70 mb-3 flex items-center gap-2">
                <Sparkles size={12} strokeWidth={2} className="text-[#D4B86A]" />
                我观察到了什么
              </h3>
              <p className="text-echo-text/65 text-[14px] leading-relaxed whitespace-pre-line">
                {currentScene.observation}
              </p>
            </div>
          </section>

          <section className="mb-5">
            <div className="glass-card rounded-card p-5">
              <h3 className="text-[14px] font-medium text-echo-text/70 mb-3 flex items-center gap-2">
                <BookOpen size={12} strokeWidth={2} className="text-[#D4B86A]" />
                {currentScene.id === "observation" ? "今天，我想替你保存这一句话" : "可以试试这样表达"}
              </h3>
              <p className="text-echo-text font-medium text-[14px] leading-relaxed whitespace-pre-line mb-4">
                {currentScene.suggestion}
              </p>
              {currentScene.id !== "observation" && (
                <div className="space-y-2">
                  <p className="text-[12px] text-echo-text/50">为什么这句话更自然？</p>
                  {currentScene.whyBetter.map((reason, i) => (
                    <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/30">
                      <span className="text-[10px] text-[#6EE7B7]">✔</span>
                      <span className="text-echo-text/60 text-[12px]">{reason}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="mb-6">
            <div className="glass-card rounded-card p-6 text-center">
              <p className="text-echo-text/35 text-[11px] mb-1">今天你练到的是</p>
              <p className="text-echo-text/70 text-[14px] font-medium">
                {currentScene.finalAbility}
              </p>
            </div>
          </section>

          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={handleSaveExpression}
              className="flex items-center justify-center gap-2 py-3 rounded-full bg-gradient-to-r from-[#DDF6E8] to-[#F2EAFF] text-echo-text font-medium text-[14px] animate-spring"
            >
              <Save size={14} strokeWidth={2} />
              {currentScene.id === "observation" ? "✨ 收录到弹药库" : "🌿 收录这句话"}
            </button>
            <button
              onClick={() => {
                setRevealedCount(0);
                setNpcVisibleUpTo(0);
                setShowHint(false);
                setAnimatingTurn(null);
                setView("chat");
              }}
              className="flex items-center justify-center gap-2 py-3 rounded-full bg-white/50 text-echo-text/60 font-medium text-[14px] animate-spring"
            >
              <RotateCcw size={14} strokeWidth={2} />
              再排练一次
            </button>
          </div>

          <button
            onClick={goBack}
            className="w-full mt-4 py-3 rounded-full text-echo-text/35 text-[13px] animate-spring"
          >
            返回排练室
          </button>
        </div>

        {showToast && (
          <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none">
            <div className="glass-card rounded-card px-6 py-4 flex items-center gap-3 animate-fade-slide pointer-events-auto">
              <div className="w-8 h-8 rounded-full bg-[#DDF6E8]/30 flex items-center justify-center">
                <span>✨</span>
              </div>
              <div>
                <p className="text-echo-text font-medium text-[14px]">已收录到弹药库</p>
                <p className="text-echo-text/40 text-[11px]">它会慢慢长成你的语言</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return null;
}